import { configureStore, ThunkAction, Action, combineReducers, Middleware } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { persistReducer, persistStore } from 'redux-persist';
import { dropNotPersisted, NOT_PERSISTED } from './persistConfig';
import AuthentificationSlice from 'features/Core/Authentification/state/authentificationSlice';
import PermissionRoleSlice from 'features/CorporationSettings/DynamicRoleSystem/state/permissionRoleSlice';
import TokenRefreshMiddleware from './refreshTokenMiddleware';
import TenantSlice from 'features/CorporationSettings/Tenant/state/tenantSlice';
import DepartmentSlice from 'features/CorporationSettings/Department/state/departmentSlice';
import PartnersSlice from 'features/BusinessConnections/Partners/state/partnerSlice';
import ContactSlice from 'features/BusinessConnections/Contacts/state/contactSlice';
// import ModalSlice from '../slices/modalSlice';
import MapSlice from "shared/slices/mapSlice";
import TemplateSlice from 'features/Templates/state/templateSlice';
import ProjectSlice from 'features/ProfitCostCenters/Project/state/projectSlice';
import EmployeeSlice from 'features/TenantSettings/Employee/state/employeeSlice';
import ApprovalSettingsSlice from 'features/TenantSettings/ApprovalSettings/state/approvalSettingsSlice';
import RequestsSlice from 'features/TenantSettings/Requests/state/requestsSlice';
import FinanceDocumentSlice from "features/Finance/FinanceDocuments/state/financeDocumentSlice";
import TextblockTemplateSlice from "features/Finance/TextblockTemplates/state/textblockTemplateSlice";
import HistoryLogSlice from 'features/HistoryLog/state/historyLogSlice';
import PaymentSlice from 'features/Finance/Payments/state/paymentSlice';
import sessionStorage from 'redux-persist/lib/storage/session';
import SettingsSlice from 'features/TableView/state/settingsSlice';
import CorporationSlice from 'features/CorporationSettings/Corporation/state/corporationSlice';
import ReferenceDataSlice from 'features/CorporationSettings/referenceDataFeatures/state/referenceDataSlice';
import NotificationSlice, { addNotification, setUnseenCount } from 'shared/slices/notificationSlice';
import ActivitiesSlice from 'features/Core/HomePage/state/activitiesSlice';
import ActivityLogsSlice from 'features/Core/HomePage/state/activityLogsSlice';
import NotesSlice from 'features/Core/HomePage/state/notesSlice';
import TasksSlice from 'features/Core/HomePage/state/tasksSlice';
import DealSlice from 'features/BusinessConnections/Deals/state/dealSlice';
import ActivityEmailSlice from 'features/Core/HomePage/state/activityEmailSlice';
import StatusNotificationsSlice, { showNotification } from 'shared/slices/statusNotificationsSlice';
import ResourcePlanningSlice from 'features/ProfitCostCenters/ResourcePlanning/state/resourcePlanningSlice';
import UnassignedDocumentsSlice from "features/UnassignedDocuments/state/unassignedDocumentsSlice";
import ApplicantTrackingSlice from 'features/ApplicantTracking/state/applicantTrackingSlice';
import UnsavedChangesSlice from 'shared/slices/unsavedChangesSlice';
import HolidaySlice from "features/TenantSettings/Holidays/state/holidaySlice";
import AbsencesSlice from 'features/Absences/state/absencesSlice';
import RefundSlice from 'features/Accounting/Refunds/state/refundSlice';
import BonusCommissionSlice from 'features/Accounting/BonusAndCommission/state/bonusCommissionSlice';
import SalaryDeductionSlice from 'features/Accounting/SalaryDeductions/state/salaryDeductionSlice';
import TimeTrackingSlice from 'features/TimeTracking/state/timeTrackingSlice';
import TenantSettingSlice from 'features/Core/TenantSetting/state/tenantSettingSlice';
import TravelPlanSlice from 'features/TravelPlan/state/travel-plan-slice';
import PayrollSlice from 'features/Payroll/state/payroll-slice';
import ProjectFilesSlice from 'features/ProfitCostCenters/ProjectFiles/state/projectFilesSlice';
import ResourceOverviewSlice from 'features/ResourceAvailabilityOverview/state/resourceAvailabilitySlice';
import TimeReportSlice from 'features/TimeReport/state/timeReportSlice';
import CommentsSlice from 'features/Core/HomePage/commentsSlice';
import CommunicationListSlice from 'features/BusinessConnections/CommunucationList/state/communication-list-slice';
import AutomatedMessageSlice from 'features/TenantSettings/AutomatedMessages/state/automatedMessageSlice';
import PatchNotesSlice from 'features/PatchNotes/state/patch-notes-slice';
import TermsSlice from 'features/Core/Terms/state/terms-slice';

const localPersistConfig = {
  key: 'local',
  storage,
  whiteList: ['AuthentificationSlice'],
}

const reducer = combineReducers({
  authentificationSlice: persistReducer(localPersistConfig, AuthentificationSlice),
  permissionRoleSlice: PermissionRoleSlice,
  corporationSlice: CorporationSlice,
  tenantSlice: TenantSlice,
  departmentSlice: DepartmentSlice,
  partnerSlice: PartnersSlice,
  contactSlice: ContactSlice,
  employeeSlice: EmployeeSlice,
  // modalSlice: ModalSlice,
  mapSlice: MapSlice,
  templateSlice: TemplateSlice,
  projectSlice: ProjectSlice,
  approvalSettingsSlice: ApprovalSettingsSlice,
  notificationSlice: NotificationSlice,
  requestsSlice: RequestsSlice,
  financeDocumentSlice: FinanceDocumentSlice,
  textblockTemplateSlice: TextblockTemplateSlice,
  historyLogSlice: HistoryLogSlice,
  paymentSlice: PaymentSlice,
  settingsSlice: SettingsSlice,
  referenceDataSlice: ReferenceDataSlice,
  activitiesSlice: ActivitiesSlice,
  activityLogsSlice: ActivityLogsSlice,
  notesSlice: NotesSlice,
  tasksSlice: TasksSlice,
  dealSlice: DealSlice,
  activityEmailSlice: ActivityEmailSlice,
  statusNotificationsSlice: StatusNotificationsSlice,
  applicantTrackingSlice: ApplicantTrackingSlice,
  resourcePlanningSlice: ResourcePlanningSlice,
  // financeDashboardSlice: FinanceDashboardSlice,
  unassignedDocumentsSlice: UnassignedDocumentsSlice,
  unsavedChangesSlice: UnsavedChangesSlice,
  commentsSlice: CommentsSlice,
  communicationListSlice: CommunicationListSlice,
  automatedMessageSlice: AutomatedMessageSlice,
  holidaySlice: HolidaySlice,
  absenceSlice: AbsencesSlice,
  refundSlice: RefundSlice,
  bonusCommissionSlice: BonusCommissionSlice,
  salaryDeductionSlice: SalaryDeductionSlice,
  travelPlanSlice: TravelPlanSlice,
  payrollSlice: PayrollSlice,
  timeTrackingSlice: TimeTrackingSlice,
  tenantSettingSlice: TenantSettingSlice,
  projectFilesSlice: ProjectFilesSlice,
  resourceOverviewSlice: ResourceOverviewSlice,
  timeReportSlice: TimeReportSlice,
  patchNotesSlice: PatchNotesSlice,
  termsSlice: TermsSlice,
});

const persistedReducer = persistReducer(
  {
    key: 'root',
    storage: sessionStorage,
    // notificationSlice is not persisted: the feed is paged by cursor, and a rehydrated list of
    // pages paired with a cursor from the previous session would duplicate rows and leave holes in
    // the feed. It is refilled on connect by the hub and on bell open by GetNotificationsPage.
    // tenantSlice and paymentSlice are not persisted (see NOT_PERSISTED): their pages load their data
    // again whenever they open, and tenantSlice's shape changed, so an old stored copy must not be
    // rehydrated into it.
    blacklist: NOT_PERSISTED,
    // A blacklisted slice saved by an older version must not be rehydrated either.
    migrate: dropNotPersisted,
  }, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const defaultMiddleware = getDefaultMiddleware({
      serializableCheck: false,
      thunk: {
        extraArgument: {
          // Add any extra arguments needed by your middleware
        },
      },
    });

    const sliceMiddleware: Middleware = (middlewareAPI) => (next) => (action) => {
      if (!action) return;

      if (action.type.startsWith('Slice/')) {
        TokenRefreshMiddleware(middlewareAPI)(next)(action);
      }
      else if (action.type === 'SignalR/ReceiveUnseenCount') {
        store.dispatch(setUnseenCount(action.payload));
      }
      else if (action.type === 'SignalR/ReceiveNewNotification') {
        store.dispatch(addNotification(action.payload));
      }
      else if (showNotification.match(action)) {
        // The Terms and Conditions gate blocks the whole app until accepted, so any toast
        // raised while it's up (most commonly the 403 background calls get for an unaccepted
        // version) would only stack up behind an undismissable modal the user can't act on.
        const { hasLoginData, termsAcceptanceRequired } = middlewareAPI.getState().authentificationSlice;
        if (!(hasLoginData && termsAcceptanceRequired)) {
          return next(action);
        }
      }
      else {
        return next(action);
      }
    };

    return defaultMiddleware.concat(sliceMiddleware);
  },
});


export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;